import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { ClearOutlined as ClearIcon, SearchOutlined as SearchIcon } from '@mui/icons-material';
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from '@mui/x-data-grid';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { customerService } from '../../../services/api';
import { COLUMNS } from '../../../pages/dashboard/leads/columns';
import GridCompactDensitySync from '../GridCompactDensitySync';

const INITIAL_OPTIONS = {
  paginationModel: { page: 0, pageSize: 25 },
  sortModel: [{ field: 'last_message_at', sort: 'desc' }],
  filterModel: { items: [] },
};

function LeadsToolbar({ onSearchChange, extra }) {
  const [inputValue, setInputValue] = useState('');
  const debounceRef = useRef(null);

  const handleChange = useCallback(
    (e) => {
      const value = e.target.value;
      setInputValue(value);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => onSearchChange(value), 400);
    },
    [onSearchChange],
  );

  const handleClear = useCallback(() => {
    clearTimeout(debounceRef.current);
    setInputValue('');
    onSearchChange('');
  }, [onSearchChange]);

  return (
    <GridToolbarContainer sx={{ px: 2, py: 1, gap: 1 }}>
      <GridCompactDensitySync />
      <TextField
        size="small"
        placeholder="Buscar..."
        value={inputValue}
        onChange={handleChange}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: inputValue ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
        sx={{ width: 360 }}
      />
      <Box sx={{ flex: 1 }} />
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      {/* <GridToolbarExport /> */}
      {extra && <Box sx={{ flexBasis: '100%' }}>{extra}</Box>}
    </GridToolbarContainer>
  );
}


/**
 * Grid de leads do dashboard, server-side. Representa um grupo de leads
 * definido por `params` (parâmetros da API, ex.: { service: 'handoff_unattended',
 * created_at__gte: '2026-09-12' }) sem saber de onde eles vieram: a página
 * de leads passa os filtros da URL; a jornada passa o trecho clicado.
 * Busca, ordenação, filtros de coluna e paginação continuam por conta da grid.
 * `toolbarExtra` aparece numa segunda linha da toolbar (ex.: chips de filtros).
 */
export default function LeadsGrid({ params, toolbarExtra }) {
  const [queryOptions, setQueryOptions] = useState(INITIAL_OPTIONS);

  // Filtros externos viram itens '=' do filterModel (?param=valor), somados aos
  // filtros de coluna que o usuário aplicar na grid.
  const requestOptions = useMemo(() => ({
    ...queryOptions,
    filterModel: {
      items: [
        ...(queryOptions.filterModel?.items ?? []),
        ...Object.entries(params ?? {}).map(([field, value]) => ({ field, operator: '=', value })),
      ],
    },
  }), [queryOptions, params]);

  const resetPage = (patch) => setQueryOptions((prev) => ({
    ...prev,
    ...patch,
    paginationModel: { ...prev.paginationModel, page: 0 },
  }));

  const onSearchChange = useCallback((search) => resetPage({ search }), []);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['dashboard-leads', requestOptions],
    queryFn: () => customerService.getConversations(requestOptions).then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  return (
    <DataGrid
      rows={data?.results ?? []}
      columns={COLUMNS}
      getRowId={(row) => row.uuid}
      rowCount={data?.count ?? 0}
      loading={isLoading || isFetching}
      paginationMode="server"
      sortingMode="server"
      filterMode="server"
      paginationModel={queryOptions.paginationModel}
      onPaginationModelChange={(paginationModel) => setQueryOptions((prev) => ({ ...prev, paginationModel }))}
      pageSizeOptions={[10, 25, 50]}
      sortModel={queryOptions.sortModel ?? []}
      onSortModelChange={(sortModel) => resetPage({ sortModel })}
      filterModel={queryOptions.filterModel ?? { items: [] }}
      onFilterModelChange={(filterModel) => resetPage({ filterModel })}
      disableRowSelectionOnClick
      ignoreDiacritics
      sx={{ border: 0 }}
      slots={{ toolbar: LeadsToolbar }}
      slotProps={{
        toolbar: { onSearchChange, extra: toolbarExtra },
        loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'linear-progress' },
      }}
    />
  );
}
